/**
 * What the SLATE says to the public, which is not what the record says to an
 * operator.
 *
 * Wave 5, defect D3. Measured on the public page 2026-08-29, rendered verbatim
 * under "NO FEED MEETS THE STANDARD":
 *
 *   picture review failed: Could not resolve authentication method. Expected
 *   one of apiKey, authToken, credentials, config, or profile to be set. Or for
 *   one of the "X-Api-Key" or "Authorization" headers to be explicitly omitted
 *
 * `onAir.reason` is written for somebody reading a log, and the slate printed
 * it straight through. The record should keep saying exactly that, because an
 * operator debugging at 7pm needs the SDK's own words. The broadcast should not.
 *
 * **Allowlist, not a filter.** A denylist of scary substrings is the version
 * that looks like it works: it passes anything nobody thought of, and the
 * unforeseen string is precisely the one worth catching. So an internal reason
 * reaches the public only by matching a known cause, and everything else falls
 * to a sentence that is honest about not being specific.
 *
 * The fallbacks are deliberately not reassuring. "Something went wrong" would
 * be a smaller lie than the SDK error but still a lie: the checks genuinely did
 * not run, nothing is being broadcast, and the page says so.
 */

/**
 * **The words here are for a person in a gym, not for a broadcaster.**
 *
 * Reported 2026-08-29: "it's not 'broadcast' its 'stream', regular people
 * 'stream' not 'broadcast'". Correct, and it applies past that one word. This
 * repository is descended from public access television and its internal
 * vocabulary is broadcast vocabulary: feeds, air, slate, take. That vocabulary
 * is right in the code, right in the docs, and right on the DIRECTOR's page,
 * which is a professional tool used by a newsroom.
 *
 * It is wrong on the public page, where the reader is somebody who has streamed
 * to their friends and has never once broadcast anything. A product that
 * explains its editorial bar in words the reader has to translate has not
 * explained it.
 */
import type { OnAir } from './types.js';
import { CLAUSES, type Clause } from './editorial.js';

export interface SlateCopy {
  /** The large mark. */
  mark: string;
  /** One sentence under it. Never an error string, never empty. */
  why: string;
  /** A clause id a viewer may be told, from `publicClause` and nowhere else. */
  clause?: string;
  /** How many fired that a viewer may not be told about. A count, never a name. */
  withheld?: number;
}

/** A cause the public can be told about, matched on the record's own wording. */
interface Known {
  /** Matched case-insensitively against the whole reason. */
  when: RegExp;
  copy: SlateCopy;
}

const NO_FEED = 'NO STREAM MEETS THE STANDARD';

const KNOWN: readonly Known[] = [
  {
    when: /^no feeds yet$/i,
    copy: { mark: 'NOBODY IS STREAMING YET', why: 'Nobody here has started a stream yet. If you can see the court, you can start one.' },
  },
  {
    when: /^connecting$/i,
    copy: { mark: 'CONNECTING', why: 'Getting you connected.' },
  },
  {
    // The audience gate is the one refusal a viewer participated in, so it is
    // the one they are owed a straight account of.
    when: /^audience:/i,
    copy: { mark: NO_FEED, why: 'Enough viewers rated the last stream badly that it was stopped.' },
  },
  {
    when: /picture/i,
    copy: { mark: NO_FEED, why: 'The picture check did not pass. Nothing is shown until it does.' },
  },
  {
    when: /speech|audio|transcript/i,
    copy: { mark: NO_FEED, why: 'The sound check did not pass. Nothing is shown until it does.' },
  },
  {
    when: /bitrate|packet loss|fps|dark|shake|freeze/i,
    copy: { mark: NO_FEED, why: 'Every stream on offer was too shaky, too dark or too choppy to show.' },
  },
  {
    when: /stale|stopped reporting|dropped/i,
    copy: { mark: NO_FEED, why: 'The stream that was playing stopped.' },
  },
  {
    when: /^director/i,
    copy: { mark: 'STOPPED', why: 'The newsroom stopped the stream.' },
  },
];

/**
 * The public sentence for an internal slate reason.
 *
 * A review that ERRORED is a rejection carrying the error everywhere else in
 * this system, and that is right: the record must not lose it. Here the error
 * is replaced rather than trimmed, because a public page has no use for an
 * error's text and every use for knowing the check did not run.
 */
/**
 * The clause a viewer may be told about, and `null` for every other case.
 *
 * P2.12's remainder, issue 024. The proposal asked for the clause id and the
 * judge's reasoning beside the slate in real time, and called it the product
 * thesis rendered as an interface. **Half of that is refused by the thesis**:
 * `slateCopy` is an allowlist because a page carrying the reviewer's own words
 * would republish what the gate refused, and **8 of the 15 rejecting clauses
 * carry `disclosure: 'operator'`** because naming one IS the disclosure it
 * exists to prevent. A child in evident distress is the example: "held under: an
 * identifiable child in evident distress" tells everybody watching exactly what
 * the clause was written to keep off the broadcast.
 *
 * So the id travels and the reasoning does not, and the id travels only for the
 * seven that may be named. **An operator clause returns `null` rather than a
 * redaction marker**, because "withheld: visual.child-distress" is the
 * disclosure with extra steps; the COUNT is published separately, which says a
 * refusal happened without saying what it was about.
 *
 * Pure, and it takes the codes rather than the reasons, so nothing carrying a
 * model's words can reach it by mistake.
 */
export function publicClause(codes: readonly string[], extra: readonly Clause[] = []): string | null {
  for (const code of codes) {
    const clause = clauseOf(code, extra);
    if (clause?.kind === 'reject' && clause.disclosure === 'public') return code;
  }
  return null;
}

/**
 * A clause by code, from the floor and from any channel clauses supplied.
 *
 * **The first version searched only `CLAUSES`, so a CHANNEL's clause was
 * invisible in both directions.** Verified: `publicClause(['live.replay'])`
 * returned null and `withheldClauses(['live.replay'])` returned 0, although
 * that clause is `disclosure: 'public'` and a refusal under it is exactly the
 * kind a viewer may be told about. So a channel refusal was reported as "there
 * was nothing to tell" when a rule had fired, which is the merged-claim defect
 * one level down.
 *
 * The channel's clauses are PASSED rather than imported, because this module is
 * shared and a client has no business reaching for a node's channel list.
 */
function clauseOf(code: string, extra: readonly Clause[]): Clause | undefined {
  return CLAUSES.find((c) => c.code === code) ?? extra.find((c) => c.code === code);
}

/**
 * How many of these a viewer may NOT be told about.
 *
 * Published beside the slate so an unnameable refusal is visible as a refusal.
 * A count is not a disclosure: it says the gate fired and says nothing about
 * whom it fired about, which is the difference the `operator` marking is for.
 */
export function withheldClauses(codes: readonly string[], extra: readonly Clause[] = []): number {
  return codes.filter((code) => {
    const clause = clauseOf(code, extra);
    return clause?.kind === 'reject' && clause.disclosure === 'operator';
  }).length;
}

export function slateCopy(reason: string): SlateCopy {
  const r = reason.trim();
  // A review that errored is matched FIRST, before the gate patterns, because
  // "picture review failed: <SDK error>" matches /picture/ and would otherwise
  // be reported as an ordinary picture refusal. It is not one: the difference
  // between a check that ruled against you and a check that could not run is
  // the whole distinction this repository is built on.
  if (/failed:|error|exception|econn|timeout|authentication|apikey|token/i.test(r)) {
    return {
      mark: NO_FEED,
      why: 'A required check could not run, so nothing is being shown.',
    };
  }
  for (const k of KNOWN) {
    if (k.when.test(r)) return k.copy;
  }
  return { mark: NO_FEED, why: 'No stream here currently meets the standard.' };
}


/**
 * What is happening behind the slate, when anything is.
 *
 * "Nobody is streaming yet" and "three streams are being checked and none has
 * passed" are very different facts, and the slate rendered them identically:
 * the count existed on the state frame and was never shown, so the one case
 * where the system is visibly WORKING looked the same as an empty room.
 *
 * Returns null when there is nothing to add, which is the common case and not
 * a failure: with no streams offered, the mark already says so.
 */
export function slateActivity(offered: number, onAir: boolean): string | null {
  if (onAir || offered <= 0) return null;
  return offered === 1
    ? '1 stream is being checked against the community standard right now.'
    : `${offered} streams are being checked against the community standard right now.`;
}

/**
 * What the PUBLIC page may say about what is on air, and the only thing it may
 * say.
 *
 * The marketing page rendered `slateCopy('no feeds yet')` at BUILD time, so
 * `switchboard.cam` said "NOBODY IS STREAMING YET" in its largest text while a
 * feed was on air, and would have kept saying it until somebody rebuilt. That
 * is a judgment the page never made: the same instrument-as-judgment class the
 * evidence island beneath it was fixed for, one heading higher.
 *
 * **The raw reason must not cross this boundary.** `onAir.reason` is written
 * for somebody reading a log, and this is the seam where a node's words become
 * a stranger's. `slateCopy` is an allowlist for exactly that, so the public
 * copy is composed HERE, on the node, and the reason itself never enters the
 * response. A site that received the reason and mapped it in the browser would
 * put the operator's words on the wire, where devtools is one keystroke away.
 */
export function airCopy(
  onAir: OnAir,
  /**
   * Can the sound source still be resolved to a path?
   *
   * **The copy must answer the same question the PLAYER answers.** `viewerStage`
   * and `remuxJobsFor` both fall back to silent when `onAir.sound` names a feed
   * that has left, while this said "two different phones" from the id alone, so
   * for the tick between a sound publisher disconnecting and `soundFor`
   * recomputing, the node served a silent output and told switchboard.cam that
   * two phones were contributing. Found by a code review 2026-08-31.
   *
   * Defaults to true so the caller that knows nothing about feeds gets today's
   * answer, and a caller that HAS the feed list is expected to pass it.
   */
  soundResolves = true,
): { onAir: 'feed' | 'slate' } & SlateCopy {
  if (onAir.kind === 'feed') {
    /*
     * Stage 2 of the picture-and-sound plan. A silent broadcast is a THIRD
     * state, not a footnote on the second, and a viewer who can see a picture
     * and hear nothing will conclude something is broken unless the page says
     * otherwise before they do.
     *
     * **It names no clause, and it never can.** Eight of the fifteen rejecting
     * clauses may not be named in public and four of the five on the speech side
     * are operator-only, so "the sound was held under: a minor was named" is
     * itself the disclosure that clause exists to prevent. Issue 024. This is a
     * fixed sentence for the same reason every other line here is fixed: the
     * reviewer's own words never reach a viewer.
     *
     * The claim it makes is exactly true and no larger. The picture met the
     * standard. The sound did not. Which rule, and what was said, are the
     * publisher's to receive and they do, through the feedback path.
     */
    if (onAir.sound === null || !soundResolves) {
      return {
        onAir: 'feed',
        mark: 'STREAMING WITHOUT SOUND',
        why: 'A stream is live here. Its picture passed every check and its sound did not, '
          + 'so the sound is held and the picture is not.',
      };
    }
    /*
     * Stage 4: the picture and the sound are from different phones.
     *
     * A viewer is told THAT, and no more. Which feeds, and why this one's own
     * sound was not used, are the publisher's and the operator's to receive: a
     * reason on a public page is a reason about somebody, and `slateCopy` is an
     * allowlist for exactly that. Issue 024.
     */
    if (onAir.sound !== onAir.feedId) {
      return {
        onAir: 'feed',
        mark: 'SOMEONE IS STREAMING NOW',
        why: 'A stream is live here. Its picture and its sound are coming from two different '
          + 'phones in the room, and both passed every check to get on.',
      };
    }
    return {
      onAir: 'feed',
      mark: 'SOMEONE IS STREAMING NOW',
      // Not "broadcasting". The reader is somebody who streams to their
      // friends, per the note at the top of this file.
      why: 'A stream is live here, and it passed every check to get on.',
    };
  }
  /*
   * P2.12's remainder: the clause travels with the copy when it may.
   *
   * `onAir.clause` is already filtered (`toSlate` is the only writer and it
   * routes through `publicClause`), so this passes it through rather than
   * filtering again: a second filter is a second copy of a decision, and the
   * one that matters is the one that cannot be bypassed. `withheld` says a
   * refusal happened without saying what it was about.
   */
  return {
    onAir: 'slate',
    ...slateCopy(onAir.reason),
    /*
     * The clause travels here too, and it is READ here now.
     *
     * These two fields were plumbed through to `/api/evidence` and that route
     * picked only `onAir`, `mark` and `why`, so the one boundary where
     * switchboard.cam renders slate copy for a stranger showed none of P2.12.
     * A value is only real if something reads it, so either this goes or the
     * route reads it; the route reads it.
     */
    ...(onAir.clause === undefined ? {} : { clause: onAir.clause }),
    ...(onAir.withheld === undefined ? {} : { withheld: onAir.withheld }),
  };
}
